import { HugeiconsIcon } from '@hugeicons/react';
import {
  BarChartIcon,
  Building03Icon,
  DollarCircleIcon,
  FileValidationIcon,
  Target01Icon,
  Add01Icon,
  Edit02Icon,
  Delete02Icon,
  CheckmarkCircle02Icon,
} from '@hugeicons/core-free-icons';
import { useCallback, useEffect, useState } from 'react';
import { api, apiError } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { DemoDataBadge } from '@/components/shared/DemoDataBadge';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { OperationalPageHeader, OperationalStatCard, operationalCardClass } from '@/components/shared/OperationalUI';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import ValueMonitoring from '@/pages/ValueMonitoring';

const VALUE_AXES = ['Personas', 'Operación', 'Productividad', 'Confianza'];
const BENEFIT_TYPES = ['Capacidad liberada', 'Ahorro evitable', 'Ingreso incremental', 'Calidad y seguridad'];
const CONFIDENCE_LEVELS = ['Baja', 'Media', 'Alta'];

const formatClp = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
    notation: value >= 1_000_000 ? 'compact' : 'standard',
  }).format(value || 0);

export default function ValueOffice() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = crear, objeto = editar

  const load = useCallback(() => {
    api.get('/benefits/ledger').then(({ data: response }) => setData(response));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (entry) => {
    setEditing(entry);
    setDialogOpen(true);
  };

  const validate = async (entry) => {
    try {
      await api.post(`/benefits/ledger/${entry.id}/validate`);
      toast.success('Beneficio validado por Finanzas');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (entry) => {
    try {
      await api.delete(`/benefits/ledger/${entry.id}`);
      toast.success('Beneficio eliminado');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) {
    return (
      <div className="mx-auto grid max-w-7xl gap-4 px-6 py-10 md:grid-cols-2">
        {[...Array(6)].map((_, index) => (
          <Skeleton key={index} className="h-48 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-10">
      <OperationalPageHeader
        title="Oficina de valor VIA"
        subtitle="Hipótesis, línea base, meta, inversión, evidencia, atribución, confianza y decisión en un solo libro."
        actions={
          <div className="flex items-center gap-2">
            <DemoDataBadge />
            {can.edit && (
              <Button onClick={openCreate} className="gap-1.5">
                <HugeiconsIcon icon={Add01Icon} className="size-4" aria-hidden="true" />
                Registrar beneficio
              </Button>
            )}
          </div>
        }
      />

      <MotionSection delay={0.04}>
        <Alert className="rounded-2xl border-primary/15 bg-primary/[0.03]">
          <HugeiconsIcon icon={FileValidationIcon} className="size-4" aria-hidden="true" />
          <AlertTitle>Libro de beneficios con trazabilidad</AlertTitle>
          <AlertDescription>
            Un beneficio solo cuenta como realizado cuando Finanzas valida fuente, período, método de atribución y
            evidencia.
          </AlertDescription>
        </Alert>
      </MotionSection>

      <StaggerContainer className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" stagger={0.04}>
        <StaggerItem>
          <OperationalStatCard
            icon={BarChartIcon}
            label="Hipótesis registradas"
            value={data.summary.entries}
            subtitle="Casos con propósito declarado"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={FileValidationIcon}
            label="Validación financiera"
            value={data.summary.validated_pct}
            suffix="%"
            subtitle="Beneficios validados"
            tone="blue"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={DollarCircleIcon}
            label="Valor anual validable"
            value={formatClp(data.summary.annual_value_clp)}
            subtitle="Sujeto a evidencia"
            tone="success"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={Building03Icon}
            label="Inversión registrada"
            value={formatClp(data.summary.investment_clp)}
            subtitle="Portafolio"
            tone="alert"
          />
        </StaggerItem>
      </StaggerContainer>

      <Tabs defaultValue="ledger" className="flex flex-col gap-6">
        <TabsList className="h-auto w-full justify-start overflow-x-auto rounded-xl bg-muted/50 p-1 sm:w-fit">
          <TabsTrigger value="ledger">Libro de beneficios</TabsTrigger>
          <TabsTrigger value="metrics">Resultados operativos</TabsTrigger>
        </TabsList>
        <TabsContent value="ledger">
          {data.entries.length === 0 ? (
            <Card className={operationalCardClass}>
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <HugeiconsIcon icon={BarChartIcon} className="size-8 text-muted-foreground/60" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">
                  Aún no hay beneficios registrados.
                  {can.edit ? ' Registre el primero para empezar a medir el valor del portafolio.' : ''}
                </p>
                {can.edit && (
                  <Button onClick={openCreate} variant="outline" className="gap-1.5">
                    <HugeiconsIcon icon={Add01Icon} className="size-4" aria-hidden="true" />
                    Registrar beneficio
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <StaggerContainer className="grid gap-5 lg:grid-cols-2" stagger={0.04}>
              {data.entries.map((entry) => {
                const attainment =
                  entry.target && entry.actual != null
                    ? Math.min(100, Math.round((entry.actual / entry.target) * 100))
                    : 0;
                const validated = entry.validation_status === 'Validado';
                return (
                  <StaggerItem key={entry.id}>
                    <Card className={operationalCardClass}>
                      <CardHeader>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <Badge variant="outline">{entry.axis}</Badge>
                            <CardTitle className="pt-3">{entry.initiative_name || 'Iniciativa sin vínculo'}</CardTitle>
                            <CardDescription>
                              {[entry.department, entry.stage].filter(Boolean).join(' · ') || 'Sin datos de iniciativa'}
                            </CardDescription>
                          </div>
                          <Badge variant={validated ? 'secondary' : 'outline'}>{entry.validation_status}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="flex flex-col gap-5">
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground">Hipótesis de valor</p>
                          <p className="mt-1 text-sm leading-relaxed">{entry.hypothesis}</p>
                        </div>
                        <div>
                          <div className="mb-2 flex justify-between gap-3 text-xs text-muted-foreground">
                            <span>{entry.metric}</span>
                            <span>{attainment}% de meta</span>
                          </div>
                          <Progress value={attainment} />
                          <div className="mt-2 flex justify-between text-xs">
                            <span>
                              Base: {entry.baseline ?? '—'} {entry.unit}
                            </span>
                            <span>
                              Actual: {entry.actual ?? '—'} / Meta: {entry.target ?? '—'} {entry.unit}
                            </span>
                          </div>
                        </div>
                        <div className="grid gap-3 rounded-xl bg-muted/50 p-4 text-xs sm:grid-cols-2">
                          <Info label="Responsable" value={entry.owner} />
                          <Info label="Confianza" value={entry.confidence} />
                          <Info
                            label="Fuente y período"
                            value={`${entry.source || 'Pendiente'} · ${entry.period || 'Pendiente'}`}
                          />
                          <Info
                            label="Valor / TCO"
                            value={`${formatClp(entry.annual_value_clp)} / ${formatClp(entry.tco_clp)}`}
                          />
                          {validated && entry.finance_validator && (
                            <Info label="Validado por (Finanzas)" value={entry.finance_validator} />
                          )}
                        </div>
                        <div className="rounded-xl border border-warning/20 bg-warning/5 p-3">
                          <p className="flex items-center gap-2 text-xs font-semibold">
                            <HugeiconsIcon icon={Target01Icon} className="size-4" aria-hidden="true" />
                            Criterio para detener o revisar
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {entry.stop_criterion || 'Pendiente de definir'}
                          </p>
                        </div>

                        {/* Acciones: editar (edit), validar y eliminar (admin) */}
                        {(can.edit || can.admin) && (
                          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
                            {can.admin && !validated && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1.5 text-xs"
                                onClick={() => validate(entry)}>
                                <HugeiconsIcon icon={CheckmarkCircle02Icon} className="size-3.5" aria-hidden="true" />
                                Validar
                              </Button>
                            )}
                            {can.edit && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1.5 text-xs"
                                onClick={() => openEdit(entry)}>
                                <HugeiconsIcon icon={Edit02Icon} className="size-3.5" aria-hidden="true" />
                                Editar
                              </Button>
                            )}
                            {can.admin && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                    aria-label="Eliminar beneficio">
                                    <HugeiconsIcon icon={Delete02Icon} className="size-3.5" aria-hidden="true" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Eliminar beneficio</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      ¿Desea eliminar esta entrada del libro de beneficios? Esta acción no se puede
                                      deshacer y queda registrada en la auditoría.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() => remove(entry)}>
                                      Eliminar
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </StaggerItem>
                );
              })}
            </StaggerContainer>
          )}
        </TabsContent>
        <TabsContent value="metrics">
          <div className="-mx-6 -my-10">
            <ValueMonitoring />
          </div>
        </TabsContent>
      </Tabs>

      <BenefitDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entry={editing}
        onSaved={() => {
          setDialogOpen(false);
          load();
        }}
      />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="mt-1 font-medium text-foreground">{value}</p>
    </div>
  );
}

// BenefitDialog crea o edita una entrada del libro de beneficios. Al editar una
// entrada validada, el backend la devuelve a "Pendiente" (requiere revalidación).
function BenefitDialog({ open, onOpenChange, entry, onSaved }) {
  const isEdit = !!entry;
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({
      initiative_id: entry?.initiative_id || '',
      axis: entry?.axis || 'Personas',
      hypothesis: entry?.hypothesis || '',
      metric: entry?.metric || '',
      unit: entry?.unit || '',
      baseline: entry?.baseline ?? '',
      target: entry?.target ?? '',
      actual: entry?.actual ?? '',
      benefit_type: entry?.benefit_type || 'Capacidad liberada',
      annual_value_clp: entry?.annual_value_clp ?? '',
      investment_clp: entry?.investment_clp ?? '',
      tco_clp: entry?.tco_clp ?? '',
      attribution_method: entry?.attribution_method || '',
      owner: entry?.owner || '',
      confidence: entry?.confidence || 'Baja',
      source: entry?.source || '',
      period: entry?.period || '',
      stop_criterion: entry?.stop_criterion || '',
    });
  }, [open, entry]);

  const set = (key, value) => setF((prev) => ({ ...prev, [key]: value }));

  const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

  const save = async () => {
    if (!f.axis || !f.hypothesis?.trim() || !f.metric?.trim() || !f.owner?.trim()) {
      toast.error('Eje, hipótesis, métrica y responsable son obligatorios');
      return;
    }
    setSaving(true);
    const payload = {
      initiative_id: f.initiative_id?.trim() || null,
      axis: f.axis,
      hypothesis: f.hypothesis.trim(),
      metric: f.metric.trim(),
      unit: f.unit?.trim() || null,
      baseline: numOrNull(f.baseline),
      target: numOrNull(f.target),
      actual: numOrNull(f.actual),
      benefit_type: f.benefit_type || null,
      annual_value_clp: numOrNull(f.annual_value_clp),
      investment_clp: numOrNull(f.investment_clp),
      tco_clp: numOrNull(f.tco_clp),
      attribution_method: f.attribution_method?.trim() || null,
      owner: f.owner.trim(),
      confidence: f.confidence || 'Baja',
      source: f.source?.trim() || null,
      period: f.period?.trim() || null,
      stop_criterion: f.stop_criterion?.trim() || null,
    };
    try {
      if (isEdit) {
        await api.put(`/benefits/ledger/${entry.id}`, payload);
        toast.success('Beneficio actualizado');
      } else {
        await api.post('/benefits/ledger', payload);
        toast.success('Beneficio registrado');
      }
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar beneficio' : 'Registrar beneficio'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Al guardar cambios, la entrada vuelve a estado "Pendiente" y requiere revalidación de Finanzas.'
              : 'Registre la hipótesis de valor, su métrica y montos. La validación por Finanzas se hace después.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldWrap label="Eje de valor" required>
              <Select value={f.axis} onValueChange={(v) => set('axis', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VALUE_AXES.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap label="Tipo de beneficio">
              <Select value={f.benefit_type} onValueChange={(v) => set('benefit_type', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BENEFIT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrap>
          </div>

          <FieldWrap label="ID de iniciativa (opcional)">
            <Input
              value={f.initiative_id}
              onChange={(e) => set('initiative_id', e.target.value)}
              placeholder="Vincula el beneficio a una iniciativa por su ID"
            />
          </FieldWrap>

          <FieldWrap label="Hipótesis de valor" required>
            <Textarea
              value={f.hypothesis}
              onChange={(e) => set('hypothesis', e.target.value)}
              rows={2}
              placeholder="Si hacemos X para Y, esperamos Z porque…"
            />
          </FieldWrap>

          <div className="grid gap-4 sm:grid-cols-2">
            <FieldWrap label="Métrica principal" required>
              <Input
                value={f.metric}
                onChange={(e) => set('metric', e.target.value)}
                placeholder="Ej.: horas liberadas por semana"
              />
            </FieldWrap>
            <FieldWrap label="Unidad">
              <Input value={f.unit} onChange={(e) => set('unit', e.target.value)} placeholder="horas, %, CLP" />
            </FieldWrap>
            <FieldWrap label="Línea base">
              <Input type="number" value={f.baseline} onChange={(e) => set('baseline', e.target.value)} />
            </FieldWrap>
            <FieldWrap label="Meta">
              <Input type="number" value={f.target} onChange={(e) => set('target', e.target.value)} />
            </FieldWrap>
            <FieldWrap label="Actual">
              <Input type="number" value={f.actual} onChange={(e) => set('actual', e.target.value)} />
            </FieldWrap>
            <FieldWrap label="Confianza">
              <Select value={f.confidence} onValueChange={(v) => set('confidence', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONFIDENCE_LEVELS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrap>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <FieldWrap label="Valor anual (CLP)">
              <Input
                type="number"
                value={f.annual_value_clp}
                onChange={(e) => set('annual_value_clp', e.target.value)}
              />
            </FieldWrap>
            <FieldWrap label="Inversión (CLP)">
              <Input type="number" value={f.investment_clp} onChange={(e) => set('investment_clp', e.target.value)} />
            </FieldWrap>
            <FieldWrap label="TCO (CLP)">
              <Input type="number" value={f.tco_clp} onChange={(e) => set('tco_clp', e.target.value)} />
            </FieldWrap>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FieldWrap label="Responsable del beneficio" required>
              <Input
                value={f.owner}
                onChange={(e) => set('owner', e.target.value)}
                placeholder="Nombre <correo@ucchristus.cl>"
              />
            </FieldWrap>
            <FieldWrap label="Método de atribución">
              <Input
                value={f.attribution_method}
                onChange={(e) => set('attribution_method', e.target.value)}
                placeholder="Cómo se atribuye el beneficio"
              />
            </FieldWrap>
            <FieldWrap label="Fuente">
              <Input value={f.source} onChange={(e) => set('source', e.target.value)} placeholder="Origen del dato" />
            </FieldWrap>
            <FieldWrap label="Período">
              <Input value={f.period} onChange={(e) => set('period', e.target.value)} placeholder="Ej.: 2026-Q1" />
            </FieldWrap>
          </div>

          <FieldWrap label="Criterio para detener o revisar">
            <Textarea
              value={f.stop_criterion}
              onChange={(e) => set('stop_criterion', e.target.value)}
              rows={2}
              placeholder="Bajo qué condición se detiene o rediseña la iniciativa"
            />
          </FieldWrap>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button disabled={saving} onClick={save}>
            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Registrar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldWrap({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}
